import { AlertTriangle } from 'lucide-react';
import { BackButton } from '@/components/shared/back-button';
import { useDiscardAttempt } from '@/api/quizzes';
import { cn } from '@/lib/utils';
import type { QuizDetail } from '@/types';

export function QuizIntro({
  quiz,
  totalQuestions,
  onStart,
  isPending,
  isError,
}: {
  quiz: QuizDetail;
  totalQuestions: number;
  onStart: () => void;
  isPending: boolean;
  isError: boolean;
}) {
  const ctx = quiz.attempt_context;
  const isLast = ctx?.is_last_attempt ?? false;
  const exhausted = ctx ? ctx.attempts_remaining <= 0 : false;
  // #32: intento sin terminar → reanudar o descartar en vez de bloquear.
  const inProgressId = ctx?.in_progress_attempt_id ?? null;
  const discard = useDiscardAttempt(quiz.id);
  const busy = isPending || discard.isPending;

  return (
    <div className="mx-auto max-w-lg space-y-6 py-12">
      <BackButton />

      {isLast && !exhausted && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Este es tu último intento</p>
            <p className="mt-1">
              Si no apruebas este intento se abrirá una acción correctiva y deberás resolverla antes
              de volver a presentar el quiz.
            </p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-8 text-center">
        <h1 className="text-2xl font-bold text-card-foreground">{quiz.title}</h1>
        <div className="mt-6 space-y-2 text-sm text-muted-foreground">
          <p>
            Puntaje mínimo para aprobar:{' '}
            <span className="font-semibold text-foreground">{quiz.passing_percent}%</span>
          </p>
          {ctx ? (
            <p>
              Intento{' '}
              <span className="font-semibold text-foreground">
                {Math.min(ctx.next_attempt_number, ctx.effective_max_attempts)}
              </span>{' '}
              de <span className="font-semibold text-foreground">{ctx.effective_max_attempts}</span>
            </p>
          ) : (
            <p>
              Intentos máximos:{' '}
              <span className="font-semibold text-foreground">{quiz.max_attempts}</span>
            </p>
          )}
          {quiz.time_limit_minutes && (
            <p>
              Tiempo límite:{' '}
              <span className="font-semibold text-foreground">{quiz.time_limit_minutes} min</span>
            </p>
          )}
          <p>
            Preguntas: <span className="font-semibold text-foreground">{totalQuestions}</span>
          </p>
        </div>
        {inProgressId ? (
          <div className="mt-8 space-y-3">
            <p className="text-sm text-muted-foreground">
              Tienes un intento sin terminar. Puedes reanudarlo para continuar donde quedaste, o
              descartarlo y empezar de nuevo.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={onStart}
                disabled={busy}
                className={cn(
                  'inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90',
                  busy && 'cursor-not-allowed opacity-60',
                )}
              >
                {isPending ? 'Preparando...' : 'Reanudar'}
              </button>
              <button
                type="button"
                onClick={() => discard.mutate(inProgressId, { onSuccess: () => onStart() })}
                disabled={busy}
                className={cn(
                  'inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent',
                  busy && 'cursor-not-allowed opacity-60',
                )}
              >
                {discard.isPending ? 'Descartando...' : 'Descartar y empezar de nuevo'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onStart}
            disabled={isPending || exhausted}
            className={cn(
              'mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90',
              (isPending || exhausted) && 'cursor-not-allowed opacity-60',
            )}
          >
            {isPending ? 'Preparando...' : exhausted ? 'Intentos agotados' : 'Iniciar Quiz'}
          </button>
        )}
        {exhausted && (
          <p className="mt-3 text-sm text-muted-foreground">
            Has agotado los intentos disponibles. Revisa tus acciones correctivas en Mi Competencia.
          </p>
        )}
        {isError && !exhausted && (
          <p className="mt-3 text-sm text-destructive">
            No se pudo iniciar el quiz. Intenta de nuevo.
          </p>
        )}
      </div>
    </div>
  );
}
